/**
 * Drawing and trajectory math for a shooting star: the pure half.
 *
 * Two things draw one. The starfield's AMBIENT shooting stars cross the sky
 * every half minute or so on a straight line, and the add-to-cart FLIGHT sends
 * one from the pressed button to the cart in the nav on a mild arc. They share
 * these helpers so the flight's star is recognisably the same object as the
 * ones in the sky: same head, same glow, same tapering trail.
 *
 * No DOM globals. Everything draws through `Ctx2D`, the handful of
 * CanvasRenderingContext2D members these calls use, so the node test runner
 * can hand in a recording mock and a real context satisfies it unchanged.
 *
 * The drawing calls reproduce the starfield's original inline code call for
 * call (same gradient geometry, same stops, same colour strings), so moving
 * the ambient stars onto them changed nothing on screen. The test pins that
 * call list; change it only on purpose.
 *
 * COLOUR. White on the dark sky, which is the default and what the pinned call
 * list uses. On a light page a white star is invisible, so light mode passes
 * STAR_INK: the same shapes and alphas in near-black, a streak of ink.
 */

export type Point = { x: number; y: number };

/** An `r, g, b` triple as the colour strings below interpolate it. */
export type StarRgb = string;
/** The dark sky's stars. */
export const STAR_WHITE: StarRgb = '255, 255, 255';
/** Light mode's stars: zinc-800, dark enough to read on the light page. */
export const STAR_INK: StarRgb = '39, 39, 42';

/** What a gradient returned by `Ctx2D` must support. `CanvasGradient` fits. */
export interface GradientLike {
  addColorStop(offset: number, color: string): void;
}

/**
 * The slice of CanvasRenderingContext2D the shooting-star helpers touch.
 *
 * The style slots are `unknown` rather than the DOM's union so a context from
 * any source is assignable without casts; the helpers only ever write them.
 */
export interface Ctx2D {
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: string;
  lineJoin: string;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void;
  fill(): void;
  stroke(): void;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): GradientLike;
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): GradientLike;
}

/** One ambient shooting star, as the starfield spawns and advances it. */
export type AmbientShootingStar = {
  x: number;
  y: number;
  /** Velocity, CSS px per ms. */
  vx: number;
  vy: number;
  /** Trail length, CSS px. */
  length: number;
  life: number;
  maxLife: number;
  thickness: number;
};

/**
 * Stroke a trail through `points`, HEAD FIRST, fading from bright at the head
 * to nothing at the tail.
 *
 * The fade is one linear gradient along the chord from head to tail, not along
 * the path. For the ambient stars the path IS the chord (two points); for the
 * cart flight's mildly curved trail the chord runs close enough to the path
 * that the fade still reads as following it, and it costs one gradient per
 * frame instead of one per segment.
 */
export function drawStarTrail(
  ctx: Ctx2D,
  points: readonly Point[],
  thickness: number,
  opacity: number,
  rgb: StarRgb = STAR_WHITE,
): void {
  if (points.length < 2) return;
  const head = points[0];
  const tail = points[points.length - 1];
  const grad = ctx.createLinearGradient(head.x, head.y, tail.x, tail.y);
  grad.addColorStop(0, `rgba(${rgb}, ${0.85 * opacity})`);
  grad.addColorStop(0.35, `rgba(${rgb}, ${0.28 * opacity})`);
  grad.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.strokeStyle = grad;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  // A no-op for a two-point trail; on a curved one it keeps the sampled
  // polyline from showing mitred corners.
  ctx.lineJoin = 'round';
  ctx.beginPath();
  // Tail to head, the order the original inline code drew in.
  ctx.moveTo(tail.x, tail.y);
  for (let i = points.length - 2; i >= 0; i--) ctx.lineTo(points[i].x, points[i].y);
  ctx.stroke();
}

/** The glowing head: a soft radial halo, then a small solid core. */
export function drawStarHead(
  ctx: Ctx2D,
  x: number,
  y: number,
  thickness: number,
  opacity: number,
  rgb: StarRgb = STAR_WHITE,
): void {
  const headRadius = thickness * 3.5;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, headRadius);
  grad.addColorStop(0, `rgba(${rgb}, ${0.9 * opacity})`);
  grad.addColorStop(0.5, `rgba(${rgb}, ${0.25 * opacity})`);
  grad.addColorStop(1, `rgba(${rgb}, 0)`);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, headRadius, 0, 2 * Math.PI);
  ctx.fill();

  ctx.fillStyle = `rgba(${rgb}, ${opacity})`;
  ctx.beginPath();
  ctx.arc(x, y, thickness * 0.55, 0, 2 * Math.PI);
  ctx.fill();
}

/**
 * One ambient shooting star: a straight trail of `s.length` behind the head
 * along its velocity, then the head over it.
 */
export function drawAmbientShootingStar(ctx: Ctx2D, s: AmbientShootingStar, opacity: number, rgb: StarRgb = STAR_WHITE): void {
  const norm = Math.hypot(s.vx, s.vy) || 1;
  const tail = { x: s.x - (s.vx / norm) * s.length, y: s.y - (s.vy / norm) * s.length };
  drawStarTrail(ctx, [{ x: s.x, y: s.y }, tail], s.thickness, opacity, rgb);
  drawStarHead(ctx, s.x, s.y, s.thickness, opacity, rgb);
}

/** Accelerating ease: slow off the mark, fastest on arrival. `t` is clamped to [0, 1]. */
export function easeInCubic(t: number): number {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return c * c * c;
}

/**
 * The point at parameter `t` (clamped to [0, 1]) on a gentle arc from `p0` to
 * `p1`: a quadratic Bezier whose control point sits off the midpoint,
 * perpendicular to the straight line.
 *
 * `bend` is the arc's greatest sideways distance from the straight line, as a
 * fraction of the straight-line distance, reached at t = 0.5 (the control
 * point sits at twice that, which is what puts the curve's peak there). 0 is a
 * straight line; about 0.1 to 0.2 reads as a mild but deliberate arc.
 * Positive bows to the right of the direction of travel as seen on screen
 * (canvas y points down), negative to the left, so a flight up and to the right
 * with a negative bend rises in an arc over the straight line.
 */
export function arcPoint(p0: Point, p1: Point, bend: number, t: number): Point {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  // The perpendicular (-dy, dx) is not normalised: its length is the distance,
  // which is exactly the scale `bend` is a fraction of.
  const cx = (p0.x + p1.x) / 2 - dy * bend * 2;
  const cy = (p0.y + p1.y) / 2 + dx * bend * 2;
  const u = 1 - c;
  return {
    x: u * u * p0.x + 2 * u * c * cx + c * c * p1.x,
    y: u * u * p0.y + 2 * u * c * cy + c * c * p1.y,
  };
}

/**
 * Points along the arc behind a head at parameter `t`, HEAD FIRST, spanning
 * `trailLenT` of curve parameter (clipped at the start, so an early trail runs
 * back to `p0` and no further). A caller that grows `trailLenT` with progress
 * gets a trail that lengthens as the star speeds up.
 *
 * `samples` is the number of points returned, at least 2, spaced evenly in
 * parameter. A span of zero returns the head twice, which `drawStarTrail`
 * strokes as nothing visible.
 */
export function trailPoints(
  p0: Point,
  p1: Point,
  bend: number,
  t: number,
  trailLenT: number,
  samples = 12,
): Point[] {
  const n = Math.max(2, Math.floor(samples));
  const head = t <= 0 ? 0 : t >= 1 ? 1 : t;
  const end = Math.max(0, head - Math.max(0, trailLenT));
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    out.push(arcPoint(p0, p1, bend, head + (end - head) * (i / (n - 1))));
  }
  return out;
}
